import { Stack, Accordion, AccordionSummary, AccordionDetails, Typography, Chip } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import dayjs from "dayjs";
import { useMedicalCareContext } from "../../../../contexts/MedicalCareContext";
import { useFhirData } from "../../../../utils/useFhirData";
import { GenericTimeline } from "../GenericTimeline";
import { TratamentoHistoryAccordion } from "./TratamentoHistoryAccordion";
import { useState } from "react";

interface IProps {
  reset: any;
  filter: any;
  setEditTreatment: (data: any) => void;
  setValue: any;
}

export function TratamentoHistory({
  reset,
  setEditTreatment,
  setValue,
}: IProps) {
  const { summaryData, loadingTreatments } = useMedicalCareContext();

  const medicationRequest = useFhirData.extractPatientSummaryResource(
    summaryData,
    "MedicationRequest"
  );

  let auxData = medicationRequest?.[0]?.entry
    ?.filter((el: any) => !el?.resource?.requester)
    ?.map((el: any, index: number) => {
      const description = el.resource?.medication?.concept?.text || "";
      const dosage = el?.resource?.dosageInstruction?.[0];
      const auxType = dosage?.doseAndRate?.[0]?.type?.text;
      const auxQtd = dosage?.doseAndRate?.[0]?.doseQuantity;
      const auxUnit = auxQtd?.unit;
      const auxUnitValue = auxQtd?.value;

      const auxTiming = dosage?.timing?.repeat;
      const auxDuration = auxTiming?.duration;
      const auxPeriod = auxTiming?.period;
      // const auxFrequency = auxTiming?.frequency;

      return {
        fullResource: el.resource,
        id: el?.resource?.id,
        awnserDate: el?.resource?.effectiveDosePeriod?.start,
        description,
        substance: el?.resource?.medication?.concept?.coding?.[0]?.display,
        dose: `${
          auxQtd?.unit && auxQtd?.value && auxType
            ? `${auxUnitValue && `${auxUnitValue}`} ${auxUnit && `${auxUnit}`}`
            : ""
        } ${
          auxPeriod
            ? `${auxPeriod && `a cada ${auxPeriod} h`} ${
                auxDuration
                  ? `durante ${auxDuration} dia(s)`
                  : "por tempo indeterminado"
              }`
            : ""
        }`,
        status: el?.resource?.status,
        note: el?.resource?.note?.[0]?.text,
      };
    });

  if (loadingTreatments) {
    auxData?.unshift({ loadingData: loadingTreatments });
  }

  // Agrupa tratamentos por dia
  const groupedByDay = auxData?.reduce((acc: any, item: any) => {
    // Ignora item de loading
    if (item.loadingData) return acc;
    
    const day = dayjs(item.awnserDate).format("DD/MM/YYYY");
    if (!acc[day]) {
      acc[day] = {
        day,
        awnserDate: item.awnserDate,
        items: [],
      };
    }
    acc[day].items.push(item);
    return acc;
  }, {}) || {};

  // Converte para array para usar na timeline
  const timelineData = Object.values(groupedByDay);

  return (
    <Stack>
      <GenericTimeline
        data={timelineData}
        component={(data: any, index: number) => (
          <DayAccordion 
            day={data.day} 
            items={data.items} 
            reset={reset}
            setEditTreatment={setEditTreatment}
            setValue={setValue}
            defaultExpanded={index === 0} 
          />
        )}
      />
    </Stack>
  );
}

// Componente Accordion que agrupa tratamentos por dia
function DayAccordion({ day, items, reset, setEditTreatment, setValue, defaultExpanded }: any) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      sx={{
        width: "100%",
        boxShadow: "1px 1px 16px 1px rgba(0, 0, 0, 0.1)",
        border: "1px solid",
        borderColor: "neutral200.main",
        borderRadius: 1,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" alignItems="center" spacing={2} width="100%">
          <Chip 
            label={`${items.length} ${items.length === 1 ? 'tratamento' : 'tratamentos'}`}
            size="small"
            sx={{ fontWeight: 500 }}
          />
        </Stack>
      </AccordionSummary>
      
      <AccordionDetails>
        <Stack spacing={1}>
          {items.map((data: any, idx: number) => (
            <TratamentoHistoryAccordion
              key={idx}
              reset={reset}
              data={data}
              index={idx}
              setEditTreatment={setEditTreatment}
              setValue={setValue}
            />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
